/**
 * pi-text-reviews – custom element
 * Filter chips, load more, optional carousel (swipe/pagination) on mobile.
 */
(function () {
  function getInitialVisible(root) {
    const val = root.getAttribute('data-initial-visible');
    return val ? Math.max(2, parseInt(val, 10)) : 6;
  }

  function getLoadMoreStep(root) {
    return getInitialVisible(root);
  }

  class PiTextReviews extends HTMLElement {
    constructor() {
      super();
      this.chips = [];
      this.cards = [];
      this.loadMoreBtn = null;
      this.loadMoreWrap = null;
      this.paginationEl = null;
      this.initialVisible = 6;
      this.loadMoreStep = 6;
      this.shownCount = 0;
      this.enableCarousel = false;
      this.carouselIndex = 0;
      this.touchStartX = 0;
      this.touchEndX = 0;
    }

    connectedCallback() {
      this.initialVisible = getInitialVisible(this);
      this.loadMoreStep = getLoadMoreStep(this);
      this.enableCarousel = this.getAttribute('data-carousel') === 'true';

      this.chips = Array.from(this.querySelectorAll('.pi-tr__chip[data-filter]'));
      this.cards = Array.from(this.querySelectorAll('.pi-tr__card[data-filter]'));
      this.loadMoreBtn = this.querySelector('[data-load-more]');
      this.loadMoreWrap = this.querySelector('.pi-tr__load-more-wrap');
      this.paginationEl = this.querySelector('.pi-tr__pagination');

      if (this.enableCarousel) {
        this.classList.add('pi-tr--carousel');
        this._buildPagination();
        this._bindCarousel();
      }

      this._bindChips();
      this._bindLoadMore();
      this._applyFilter(this._getActiveFilter());
    }

    _getActiveFilter() {
      const active = this.querySelector('.pi-tr__chip--active');
      return active ? active.getAttribute('data-filter') : 'all';
    }

    _bindChips() {
      this.chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          const filter = chip.getAttribute('data-filter');
          this.chips.forEach((c) => c.classList.remove('pi-tr__chip--active'));
          chip.classList.add('pi-tr__chip--active');
          this._applyFilter(filter);
        });
      });
    }

    _getFilteredCards(filter) {
      if (filter === 'all') return this.cards.slice();
      return this.cards.filter((card) => card.getAttribute('data-filter') === filter);
    }

    _applyFilter(filter) {
      const filtered = this._getFilteredCards(filter);
      this.cards.forEach((card) => {
        const show = filtered.includes(card);
        card.classList.toggle('pi-tr__card--hidden', !show);
      });
      this.shownCount = 0;
      this._applyLoadMoreVisibility(filtered);
      if (this.enableCarousel) {
        this._updatePagination(filtered.length);
        this._scrollCarouselTo(0);
      }
    }

    _applyLoadMoreVisibility(filteredCards) {
      const total = filteredCards.length;
      const toShow = Math.min(this.initialVisible + this.shownCount, total);
      filteredCards.forEach((card, idx) => {
        card.classList.toggle('pi-tr__card--load-more-hidden', idx >= toShow);
      });
      this.shownCount = Math.max(0, toShow - this.initialVisible);
      if (!this.loadMoreWrap) return;
      if (total <= this.initialVisible || toShow >= total) {
        this.loadMoreWrap.classList.add('pi-tr__load-more-wrap--hidden');
      } else {
        this.loadMoreWrap.classList.remove('pi-tr__load-more-wrap--hidden');
      }
    }

    _bindLoadMore() {
      if (!this.loadMoreBtn) return;
      this.loadMoreBtn.addEventListener('click', () => {
        const filter = this._getActiveFilter();
        const filtered = this._getFilteredCards(filter);
        this.shownCount += this.loadMoreStep;
        this._applyLoadMoreVisibility(filtered);
      });
    }

    _buildPagination() {
      if (!this.paginationEl || !this.enableCarousel) return;
      this.paginationEl.innerHTML = '';
      const count = this._getFilteredCards('all').length;
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pi-tr__pagination-dot' + (i === 0 ? ' pi-tr__pagination-dot--active' : '');
        dot.setAttribute('aria-label', 'Go to review ' + (i + 1));
        dot.dataset.index = String(i);
        dot.addEventListener('click', () => this._scrollCarouselTo(i));
        this.paginationEl.appendChild(dot);
      }
    }

    _updatePagination(visibleCount) {
      if (!this.paginationEl) return;
      this.paginationEl.innerHTML = '';
      for (let i = 0; i < visibleCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pi-tr__pagination-dot' + (i === 0 ? ' pi-tr__pagination-dot--active' : '');
        dot.setAttribute('aria-label', 'Go to review ' + (i + 1));
        dot.dataset.index = String(i);
        dot.addEventListener('click', () => this._scrollCarouselTo(i));
        this.paginationEl.appendChild(dot);
      }
    }

    _scrollCarouselTo(index) {
      const filter = this._getActiveFilter();
      const filtered = this._getFilteredCards(filter);
      const grid = this.querySelector('.pi-tr__grid');
      if (!grid || index < 0 || index >= filtered.length) return;
      const card = filtered[index];
      if (card) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
      this.paginationEl.querySelectorAll('.pi-tr__pagination-dot').forEach((dot, i) => {
        dot.classList.toggle('pi-tr__pagination-dot--active', i === index);
      });
      this.carouselIndex = index;
    }

    _bindCarousel() {
      const grid = this.querySelector('.pi-tr__grid');
      if (!grid) return;

      grid.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      grid.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        const diff = this.touchStartX - this.touchEndX;
        const filter = this._getActiveFilter();
        const filtered = this._getFilteredCards(filter);
        if (filtered.length <= 1) return;
        if (Math.abs(diff) < 50) return;
        let next = this.carouselIndex;
        if (diff > 0) next = Math.min(this.carouselIndex + 1, filtered.length - 1);
        else next = Math.max(this.carouselIndex - 1, 0);
        if (next !== this.carouselIndex) {
          this.carouselIndex = next;
          this._scrollCarouselTo(next);
        }
      }, { passive: true });

      grid.addEventListener('scroll', () => {
        if (!this.paginationEl) return;
        const filter = this._getActiveFilter();
        const filtered = this._getFilteredCards(filter);
        const scrollLeft = grid.scrollLeft;
        const cardWidth = filtered[0] ? filtered[0].offsetWidth : 0;
        const gap = 16;
        const index = Math.round(scrollLeft / (cardWidth + gap));
        const clamped = Math.max(0, Math.min(index, filtered.length - 1));
        this.carouselIndex = clamped;
        this.paginationEl.querySelectorAll('.pi-tr__pagination-dot').forEach((dot, i) => {
          dot.classList.toggle('pi-tr__pagination-dot--active', i === clamped);
        });
      }, { passive: true });
    }
  }

  if (!customElements.get('pi-text-reviews')) {
    customElements.define('pi-text-reviews', PiTextReviews);
  }
})();
